import React from 'react';

interface AvatarProps {
    /** URL de la foto. Si falta o falla, se cae a la inicial. */
    src?: string | null;
    /** Nombre para sacar la inicial y describir la imagen. */
    name?: string | null;
    /** Clases del contenedor: tamaño, radio y color de respaldo. */
    className?: string;
    /** Clases del cuadro con la inicial (cuando no hay foto). */
    fallbackClassName?: string;
}

/**
 * Avatar — foto de perfil con respaldo.
 *
 * Resuelve dos cosas que la app tenía sueltas en los siete sitios donde se
 * pinta una foto:
 *
 * 1. `referrerPolicy="no-referrer"`. Las fotos de Google llegan de
 *    `lh3.googleusercontent.com`, que responde 403 cuando le llega un referrer
 *    que no reconoce, y además limita por volumen. Sin esto, la foto se cae
 *    sola cada tanto sin que nadie haya tocado nada.
 *
 * 2. Respaldo si la imagen falla. Antes, una foto caída dejaba el ícono de
 *    imagen rota del navegador — que parece un error de la app. Ahora degrada a
 *    la inicial, igual que si el usuario nunca hubiera subido foto.
 *
 * El intento se reinicia cuando cambia la URL, para que una foto nueva (o la
 * misma ya funcionando) tenga otra oportunidad sin recargar.
 */
export const Avatar: React.FC<AvatarProps> = ({
    src, name, className = '', fallbackClassName = '',
}) => {
    const [fallo, setFallo] = React.useState(false);
    React.useEffect(() => { setFallo(false); }, [src]);

    const nombre = name || 'U';
    const hayFoto = !!src && !fallo;

    return (
        <div className={`overflow-hidden shrink-0 ${className}`}>
            {hayFoto ? (
                <img
                    src={src as string}
                    alt=""
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={() => setFallo(true)}
                />
            ) : (
                <div className={`w-full h-full flex items-center justify-center font-semibold ${fallbackClassName}`}>
                    {nombre.charAt(0).toUpperCase()}
                </div>
            )}
        </div>
    );
};

export default Avatar;
